<script lang="ts">
  import { onMount } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiCheck, mdiEraser } from "@mdi/js"
  import { renderSVG } from "uqr"
  import SignaturePhotoUploadAction from "./_modules/signature-photo-upload-action"
  import type { SignaturePhotoUploadCompleted } from "./_shared/_logic/signature-contract"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
  }

  let {
    initialSignature = null,
    mobileSigningUrl = null,
    organizationId,
    onSave,
    onPhotoUploaded,
    onremoveOverlay = null,
    onshowOverlay = null,
  }: {
    initialSignature?: OrganizationLetterSignature | null
    mobileSigningUrl?: string | null
    organizationId: string
    onSave: (signature: OrganizationLetterSignature) => Promise<void> | void
    onPhotoUploaded: SignaturePhotoUploadCompleted
  } & OverlayHostProps = $props()

  const widthPx = 600
  const heightPx = 180

  let canvas: HTMLCanvasElement
  let drawing = false
  let hasInk = $state(false)
  let saving = $state(false)
  const mobileSigningQrCodeUrl = $derived(
    mobileSigningUrl
      ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
          renderSVG(mobileSigningUrl, { ecc: "M", border: 4 }),
        )}`
      : null,
  )

  const context = () => canvas?.getContext("2d") ?? null

  const position = (event: PointerEvent) => {
    const rect = canvas.getBoundingClientRect()

    return {
      x: ((event.clientX - rect.left) / rect.width) * widthPx,
      y: ((event.clientY - rect.top) / rect.height) * heightPx,
    }
  }

  const setupContext = () => {
    const ctx = context()

    if (!ctx) return

    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.lineWidth = 4
    ctx.strokeStyle = "#111827"
  }

  const clearCanvas = () => {
    const ctx = context()

    if (!ctx) return

    ctx.clearRect(0, 0, widthPx, heightPx)
    setupContext()
    hasInk = false
  }

  const startDrawing = (event: PointerEvent) => {
    const ctx = context()

    if (!ctx) return

    canvas.setPointerCapture?.(event.pointerId)
    const point = position(event)
    ctx.beginPath()
    ctx.moveTo(point.x, point.y)
    drawing = true
    hasInk = true
  }

  const draw = (event: PointerEvent) => {
    if (!drawing) return

    const ctx = context()

    if (!ctx) return

    const point = position(event)
    ctx.lineTo(point.x, point.y)
    ctx.stroke()
  }

  const stopDrawing = (event: PointerEvent) => {
    if (!drawing) return

    drawing = false
    canvas.releasePointerCapture?.(event.pointerId)
  }

  const loadInitialSignature = () => {
    if (!initialSignature?.image_png_base64) return

    const image = new Image()
    image.onload = () => {
      const ctx = context()

      if (!ctx) return

      ctx.clearRect(0, 0, widthPx, heightPx)
      ctx.drawImage(image, 0, 0, widthPx, heightPx)
      setupContext()
      hasInk = true
    }
    image.src = `data:image/png;base64,${initialSignature.image_png_base64}`
  }

  const save = async () => {
    if (!hasInk || saving) return

    saving = true
    try {
      const dataUrl = canvas.toDataURL("image/png")
      await onSave({
        image_png_base64: dataUrl.replace(/^data:image\/png;base64,/, ""),
        width_px: widthPx,
        height_px: heightPx,
      })
      onremoveOverlay?.()
    } finally {
      saving = false
    }
  }

  const handlePhotoUploaded: SignaturePhotoUploadCompleted = (policy) => {
    onPhotoUploaded(policy)
    onremoveOverlay?.()
  }

  onMount(() => {
    onshowOverlay?.()
    setupContext()
    loadInitialSignature()
  })
</script>

<section class="signature-overlay">
  <SignaturePhotoUploadAction {organizationId} onUploaded={handlePhotoUploaded} />
  {#if mobileSigningQrCodeUrl}
    <aside class="mobile-signing-callout" aria-labelledby="mobile-signing-heading">
      <div class="mobile-signing-copy">
        <h3 id="mobile-signing-heading">Lieber am Handy unterschreiben?</h3>
        <p>
          Scannen Sie den QR-Code mit Ihrem Handy. Melden Sie sich dort wie gewohnt an; der
          Signaturdialog öffnet sich anschließend automatisch.
        </p>
        <p
          >Tippen Sie auf „Übernehmen“ und anschließend auf „Speichern“ auf der Seite. Laden Sie
          danach diese Seite am Rechner neu.</p
        >
      </div>
      <div class="qr-code-frame">
        <img
          src={mobileSigningQrCodeUrl}
          alt="QR-Code zum Öffnen der Unterschrift auf dem Handy"
          width="144"
          height="144"
        />
      </div>
    </aside>
  {/if}
  <p class="mobile-signing-hint">Zeichnen Sie Ihre Unterschrift mit dem Finger in das Feld.</p>
  <p>
    Zeichnen Sie die Unterschrift so, wie sie auf Terminbriefen erscheinen soll. Der Hintergrund
    bleibt im übernommenen Bild transparent. Erst „Speichern“ auf der Seite hinterlegt die
    Unterschrift dauerhaft.
  </p>
  <div class="canvas-frame">
    <canvas
      bind:this={canvas}
      width={widthPx}
      height={heightPx}
      aria-label="Unterschrift zeichnen"
      onpointerdown={startDrawing}
      onpointermove={draw}
      onpointerup={stopDrawing}
      onpointercancel={stopDrawing}
    ></canvas>
  </div>
  <div class="actions">
    <CTA
      type="secondaryButton"
      content="Zeichenfläche leeren"
      onClick={clearCanvas}
      disabled={saving}
    >
      {#snippet iconSnippet()}
        <Icon path={mdiEraser} />
      {/snippet}
    </CTA>
    <CTA
      type="tertiaryButton"
      content="Abbrechen"
      onClick={() => onremoveOverlay?.()}
      disabled={saving}
    />
    <CTA
      type="primaryButton"
      content={saving ? "Übernehme…" : "Übernehmen"}
      onClick={save}
      disabled={saving || !hasInk}
    >
      {#snippet iconSnippet()}
        <Icon path={mdiCheck} />
      {/snippet}
    </CTA>
  </div>
</section>

<style lang="less">
  .signature-overlay {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    color: var(--text-100);
  }

  .signature-overlay p {
    margin: 0;
    color: var(--text-80);
  }

  .mobile-signing-callout {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.25rem;
    padding-left: 1.2rem;
    border: 1px solid white;
    border-radius: 0.75rem;
    background: color-mix(in srgb, var(--text-invers-100) 5%, transparent);
  }

  .mobile-signing-copy {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 32rem;
  }

  .mobile-signing-copy h3 {
    margin: 0;
    color: var(--text-100);
    font-size: 1rem;
  }

  .qr-code-frame {
    flex: 0 0 auto;
    padding: 0.4rem;
    border-radius: 0.5rem;
    background: #fff;
    line-height: 0;
  }

  .qr-code-frame img {
    display: block;
    width: 144px;
    height: 144px;
  }

  .mobile-signing-hint {
    display: none;
  }

  .canvas-frame {
    border: 1px solid var(--text-invers-60);
    background:
      linear-gradient(45deg, #f6f7f8 25%, transparent 25%),
      linear-gradient(-45deg, #f6f7f8 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #f6f7f8 75%),
      linear-gradient(-45deg, transparent 75%, #f6f7f8 75%);
    background-color: #fff;
    background-size: 20px 20px;
    background-position:
      0 0,
      0 10px,
      10px -10px,
      -10px 0;
    width: min(100%, 680px);
    aspect-ratio: 10 / 3;
  }

  canvas {
    width: 100%;
    height: 100%;
    display: block;
    touch-action: none;
    cursor: crosshair;
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
  }

  @media (max-width: 767px), (pointer: coarse) {
    .mobile-signing-callout {
      display: none;
    }

    .mobile-signing-hint {
      display: block;
    }

    canvas {
      cursor: default;
    }
  }
</style>
